// „Инвентаризация" — ОПЕРАЦИЯТА: начален екран вместо таблица (таблицата е в
// справката „Инвентаризации"). Показва незавършената ми инвентаризация и тези
// на колегите (черновите от сървъра; до BE без тях — локалната в този
// браузър; заета → „Поеми редакцията") и последната инвентаризация на всеки
// склад с „Започни" за него. „+ Нова инвентаризация" / „Започни" при
// незавършена питат: продължи я или я изтрий. След запис листът се връща тук
// със `{openDoc}` → toast „Виж документа".
//
// Незавършеното броене е КРИТИЧЕН сигнал (при всички потребители): картата е
// червена, складът в „Последна инвентаризация" носи червено хапче и „Продължи"
// / „Поеми", а „Започни" / „+ Нова" за склад, който колега брои, пита
// (`useColleagueCountQuestion`) — продължи неговото или започни отделно.
import { useEffect, useMemo, type FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import { useWarehouses } from "../../../hooks/useInventory";
import {
  useLatestStocktakes,
  useStocktakeDocuments,
} from "../../../hooks/useInventoryStocktakes";
import type { Stocktake } from "../../../api/inventory";
import { PageHeader } from "../components/list/PageHeader";
import { PRIMARY_BUTTON_CLASS, SECONDARY_BUTTON_CLASS } from "../components/list/buttonStyles";
import { InventoryTheme } from "../components/InventoryTheme";
import { useActionToast } from "../components/useActionToast";
import { useInventoryShell, type LeaveGuard } from "../components/shell/InventoryShellContext";
import { DraftHomeSection } from "../components/drafts/DraftHomeSection";
import { DraftHomeModals } from "../components/drafts/DraftHomeModals";
import { DRAFT_TEXTS } from "../components/drafts/draftTexts";
import { useStocktakeDocument } from "./components/useStocktakeDocument";
import {
  LatestByWarehouseSection,
  type WarehouseStocktakeRow,
} from "./components/home/StocktakeHomeSections";
import { useStocktakeHomeDrafts } from "./components/home/useStocktakeHomeDrafts";
import { isMineDraft, unfinishedByWarehouse } from "./components/home/stocktakeUnfinished";
import { useColleagueCountQuestion } from "./components/home/useColleagueCountQuestion";

// Inventory.tsx подава на всяка страница общите props на layout-а; началният
// екран чете нужното от `useInventoryShell`/`useViewport` и ги игнорира.
type LayoutProps = {
  onOpenMobileMenu?: () => void;
  isNarrow?: boolean;
  warehouseId?: number | null;
  warehouseName?: string | null;
  onRegisterLeaveGuard?: (guard: LeaveGuard | null) => void;
};

export const StocktakeHomePage: FC<LayoutProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isNarrow } = useViewport();
  const { warehouseId: shellWarehouseId } = useInventoryShell();
  const showToast = useActionToast();
  const stocktakeDocument = useStocktakeDocument();
  const { data: warehouses = [], isSuccess: warehousesLoaded } = useWarehouses();

  // ─── Незавършените: моята (сървър / този браузър) и на колегите ──────────
  const drafts = useStocktakeHomeDrafts();

  // ─── Последната инвентаризация по склад ─────────────────────────────────
  const latestQuery = useLatestStocktakes();
  const latestList = useMemo(() => latestQuery.data ?? [], [latestQuery.data]);
  const documentQueries = useStocktakeDocuments(latestList.map((latest) => latest.stocktakeId));
  const documentById = new Map<number, Stocktake>();
  documentQueries.forEach((query) => {
    if (query.data) documentById.set(query.data.id, query.data);
  });
  const latestByWarehouse = new Map(latestList.map((latest) => [latest.warehouseId, latest]));
  // Незавършените броения по склад — моята първа
  const unfinishedDrafts = unfinishedByWarehouse(drafts.mine, drafts.others, warehouses);
  const unfinishedOf = (warehouseId: number) =>
    (unfinishedDrafts.get(warehouseId) ?? []).map((draft) => ({
      draft,
      mine: isMineDraft(draft, drafts.mine),
    }));
  // Всички складове (и тези без инвентаризация), по реда на справочника
  const warehouseRows: WarehouseStocktakeRow[] = warehouses.map((warehouse) => {
    const latest = latestByWarehouse.get(warehouse.id);
    return {
      warehouseId: warehouse.id,
      warehouseName: warehouse.name,
      latest: latest
        ? {
            stocktakeId: latest.stocktakeId,
            date: latest.date,
            createdByName: latest.createdByName,
            document: documentById.get(latest.stocktakeId) ?? null,
          }
        : null,
      unfinished: unfinishedOf(warehouse.id),
    };
  });

  // ─── Нова инвентаризация: при незавършена — питаме (`drafts.ask`) ───────
  // складът („Започни" за него) минава и през въпроса; null — по подразбиране
  // (листът тръгва от текущия склад на модула). Брои ли го колега — първо
  // питаме дали да продължим неговото броене.
  const colleagueQuestion = useColleagueCountQuestion({
    unfinishedOf,
    warehouseNameOf: (warehouseId) =>
      warehouses.find((warehouse) => warehouse.id === warehouseId)?.name ?? `#${warehouseId}`,
  });
  const startFor = (warehouseId: number | null) =>
    colleagueQuestion.guard(warehouseId ?? shellWarehouseId, () =>
      drafts.startNew(warehouseId != null ? { warehouseId } : undefined),
    );

  // След запис: state.openDoc → toast с „Виж документа"
  useEffect(() => {
    const state = location.state as { openDoc?: Stocktake } | null;
    if (!state?.openDoc) return;
    const saved = state.openDoc;
    navigate(location.pathname, { replace: true, state: null });
    drafts.refreshLocal();
    showToast({
      text: t("inventoryPage.stocktakeSavedToast", { number: saved.id }),
      actionLabel: t("inventoryPage.postingViewDocument"),
      onAction: () => stocktakeDocument.open(saved, { editable: false }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  return (
    <InventoryTheme>
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          title={t("inventoryPage.stocktake")}
          subtitle={drafts.mine ? t("inventoryPage.stocktakeHomeHasDraft") : undefined}
          right={
            <>
              <button
                type="button"
                onClick={() => navigate("/inventory/stocktakes")}
                className={SECONDARY_BUTTON_CLASS}
              >
                {t("inventoryPage.stocktakeHomeAll")}
              </button>
              <button type="button" onClick={() => startFor(null)} className={PRIMARY_BUTTON_CLASS}>
                {t("inventoryPage.newStocktakePlus")}
              </button>
            </>
          }
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-[26px] overflow-y-auto ${
            isNarrow ? "px-3 pb-10 pt-3.5" : "px-7 pb-12 pt-5"
          }`}
        >
          <DraftHomeSection
            mine={drafts.mine}
            others={drafts.others}
            texts={DRAFT_TEXTS.stocktake}
            onNew={() => startFor(null)}
            tone="critical"
          />
          <LatestByWarehouseSection
            rows={warehouseRows}
            warehousesLoaded={warehousesLoaded}
            latestKnown={latestQuery.isSuccess}
            onStart={startFor}
            onView={(stocktakeId) => {
              const loaded = documentById.get(stocktakeId);
              if (loaded) stocktakeDocument.open(loaded);
              else void stocktakeDocument.openById(stocktakeId);
            }}
          />
        </div>

        <DraftHomeModals drafts={drafts} texts={DRAFT_TEXTS.stocktake} />
        {colleagueQuestion.modal}
        {stocktakeDocument.modals}
      </div>
    </InventoryTheme>
  );
};

export default StocktakeHomePage;
